import React, { useEffect, useState, useContext } from 'react';
import { AppContext } from '../window';
import SetPasswordModal from './SetPasswordModal';

const STORAGE_KEY = 'pendingTempPassword';

/**
 * Retained for backwards compatibility -- EmailFirstSignup may still call this
 * during its existing flow but the modal trigger no longer relies on it. The
 * canonical signal is now user.credentials.passwordSetByUser === 0.
 */
export const setPendingTempPassword = (_password: string): void => {
    try {
        // Clear any stale value from previous sessions; we no longer store the
        // temp password client-side because the server tracks it via the
        // passwordSetByUser flag.
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
};

/**
 * Globally-mounted component that surfaces the SetPasswordModal whenever the
 * logged-in user has not yet chosen their own password (i.e. accounts created
 * via the email-first landing flow, or who opened a reset link). Users can
 * dismiss the modal for the browser session ("Maybe later"); it reappears in
 * the next one until they set a password or log in with their password.
 */
const RESET_FLOW_KEY = 'passwordResetFlow';

const isStoredResetFlow = (): boolean => {
    try {
        return window.sessionStorage.getItem(RESET_FLOW_KEY) === '1';
    } catch {
        return false;
    }
};

// "Maybe later" lasts the browser session: the modal used to come back on
// every page load. Stored per member, so another login on the device asks again.
const DISMISSED_KEY = 'pendingPasswordDismissed';

const isStoredDismissed = (userId: number | undefined): boolean => {
    try {
        return userId !== undefined && window.sessionStorage.getItem(DISMISSED_KEY) === String(userId);
    } catch {
        return false;
    }
};

const storeDismissed = (userId: number | undefined): void => {
    try {
        if (userId !== undefined) {
            window.sessionStorage.setItem(DISMISSED_KEY, String(userId));
        }
    } catch {
        // ignore: the dismissal then lasts until the next page load
    }
};

const PendingPasswordPrompt: React.FC = () => {
    const { user, redirectRoute } = useContext(AppContext);
    const [dismissed, setDismissed] = useState(() => isStoredDismissed(user?.id));

    // Re-read the dismissal whenever the user changes (e.g. login/logout).
    useEffect(() => {
        setDismissed(isStoredDismissed(user?.id));
    }, [user?.id]);

    // Arriving via a reset-password e-mail link should show the reset copy
    // rather than the email-first signup copy. The server says so
    // (credentials.passwordFromReset, a session flag set where the link is
    // handled: the SPA lands on `/`, never on the reset route). Persist it for
    // the tab session so the copy stays correct when the modal reappears, and
    // clear it once a password has been set.
    const fromReset = (user?.credentials as { passwordFromReset?: boolean } | undefined)?.passwordFromReset === true;
    const isResetFlow = fromReset || redirectRoute === '/user/credentials/reset-password' || isStoredResetFlow();
    useEffect(() => {
        try {
            if (user?.credentials?.passwordSetByUser === 0 && isResetFlow) {
                window.sessionStorage.setItem(RESET_FLOW_KEY, '1');
            } else {
                window.sessionStorage.removeItem(RESET_FLOW_KEY);
            }
        } catch {
            // ignore
        }
    }, [user?.credentials?.passwordSetByUser, isResetFlow]);

    if (!user || !user.credentials) {
        return null;
    }
    if (user.credentials.passwordSetByUser !== 0) {
        return null;
    }
    if (dismissed) {
        return null;
    }

    return (
        <SetPasswordModal
            show={true}
            onClose={() => {
                storeDismissed(user.id);
                setDismissed(true);
            }}
            variant={isResetFlow ? 'reset' : 'signup'}
        />
    );
};

export default PendingPasswordPrompt;
